<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import CheckBox from "@/components/ui/CheckBox.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";

const adminStore = useAdminStore();
const toast = useToast();

const items = computed(() => adminStore.priceListItems);

const editing = ref(null);
const showForm = ref(false);
const form = reactive(defaultForm());

function defaultForm() {
    return {
        product_id: "",
        sale_price: 0,
        old_price: 0,
        comment: "",
        info: "",
        is_promoted: false,
        rating: 0,
        qnt: 0,
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
}

function openCreate() {
    editing.value = {};
    showForm.value = true;
    resetForm();
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        product_id: item.product_id || "",
        sale_price: item.sale_price ?? 0,
        old_price: item.old_price ?? 0,
        comment: item.comment || "",
        info: item.info || "",
        is_promoted: !!item.is_promoted,
        rating: item.rating ?? 0,
        qnt: item.qnt ?? 0,
    });
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    if (!form.product_id) {
        toast.error("Выберите товар");
        return;
    }
    const payload = {
        product_id: form.product_id,
        sale_price: Number(form.sale_price),
        old_price: Number(form.old_price),
        comment: form.comment,
        info: form.info,
        is_promoted: form.is_promoted,
        rating: Number(form.rating),
        qnt: Number(form.qnt),
    };
    const isEdit = !!editing.value?.id;
    const ok = isEdit
        ? await adminStore.updateItem('price-list-items', editing.value.id, payload)
        : await adminStore.createItem('price-list-items', payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success(isEdit ? "Позиция прайс-листа обновлена" : "Позиция прайс-листа создана");
        cancelForm();
        await adminStore.loadList('price-list-items');
    }
}

async function deleteHandler(item) {
    const productName = item.product_name || item.product_id || item.id;
    if (!confirm(`Удалить позицию прайс-листа (продукт: "${productName}")?`)) return;
    const ok = await adminStore.deleteItem('price-list-items', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Позиция прайс-листа удалена");
        await adminStore.loadList('price-list-items');
    }
}

onMounted(async () => {
    await Promise.all([
        adminStore.loadList('price-list-items'),
        adminStore.loadProductsData({page: 1, per_page: 100}),
    ]);
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Прайс-лист</div>
      <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
        <template #caption>Добавить позицию</template>
      </ActionButton>
    </div>

    <AdminModal :visible="showForm" :title="editing?.id ? 'Редактирование позиции прайс-листа' : 'Новая позиция прайс-листа'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field">
          <label class="adminCrud__label">Товар</label>
          <select v-model="form.product_id" class="adminCrud__select">
            <option value="">— Выберите товар —</option>
            <option v-for="product in adminStore.products" :key="product.id" :value="product.id">
              {{ product.nm || product.id }}
            </option>
          </select>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Цена продажи</label>
          <form-input v-model="form.sale_price"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Старая цена</label>
          <form-input v-model="form.old_price"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Рейтинг</label>
          <form-input v-model="form.rating"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Количество</label>
          <form-input v-model="form.qnt"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Комментарий</label>
          <form-input v-model="form.comment" rows="3"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Информация</label>
          <form-input v-model="form.info" rows="3"/>
        </div>
      </div>
      <div class="adminCrud__checks">
        <check-box v-model="form.is_promoted" text="Продвижение"/>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <table v-else-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th>Продукт</th>
        <th>Действия</th>
        <th>Цена продажи</th>
        <th>Старая цена</th>
        <th>Рейтинг</th>
        <th>Кол-во</th>
        <th>Продвижение</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>{{ item.product_name || item.product_id || '—' }}</td>
        <td class="adminCrud__actions">
          <button class="adminCrud__actionButton" title="Изменить" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminCrud__actionButton adminCrud__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
        <td>{{ item.sale_price ?? '—' }}</td>
        <td>{{ item.old_price ?? '—' }}</td>
        <td>{{ item.rating ?? '—' }}</td>
        <td>{{ item.qnt ?? '—' }}</td>
        <td>{{ item.is_promoted ? 'Да' : 'Нет' }}</td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__form {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
  }

  &__formTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }
  }

  &__checks {
    display: flex;
    gap: 24px;
  }

  &__formActions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
